import React, { useState, useEffect } from 'react';
import { X, ThermometerSnowflake, Thermometer, GaugeCircle, AlertCircle, Trash2 } from 'lucide-react';
import { Starter, STARTER_TEXTURES, MANAGEMENT_STRATEGIES, TEXTURE_RATIOS } from '../../types/baking';
import { TextureSelector } from './TextureSelector';
import { StorageSelector } from './StorageSelector';
import { ManagementStrategySelector } from './ManagementStrategySelector';
import { updateStarter, deleteStarter } from '../../lib/store';
import { getTemperatureWarning, checkStarterHealth } from '../../lib/utils';
import { StarterActivityGraph } from './StarterActivityGraph';
import { Recycle } from 'lucide-react';
import { auth, db } from '../../lib/firebase';
import { collection, query, where, getDocs, updateDoc, doc } from 'firebase/firestore';

interface StarterDetailsModalProps {
  starter: Starter | null;
  starters: Starter[];
  onClose: () => void;
  onUpdate?: (id: string, updates: Partial<Starter>) => void;
}

export function StarterDetailsModal({ starter, starters, onClose, onUpdate }: StarterDetailsModalProps) {
  const [isEditing, setIsEditing] = useState(false);
  const [loading, setLoading] = useState(false);
  const [recentlyFed, setRecentlyFed] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [formData, setFormData] = useState<Omit<Starter, 'id' | 'createdAt'>>({
    name: '',
    texture: 'liquid',
    feedingRatio: '1-1-1',
    flourType: '',
    storage: 'room',
    lastFed: new Date(),
    isFavorite: false,
    managementStrategy: 'normal',
    roomTemperature: 20
  });

  useEffect(() => {
    if (starter) {
      setFormData({
        name: starter.name,
        texture: starter.texture,
        feedingRatio: starter.feedingRatio,
        flourType: starter.flourType,
        storage: starter.storage,
        lastFed: starter.lastFed,
        isFavorite: starter.isFavorite,
        managementStrategy: starter.managementStrategy || 'normal',
        roomTemperature: starter.roomTemperature
      });
    }
  }, [starter]);

  // Update feeding ratio when texture changes
  useEffect(() => {
    if (isEditing) {
      const defaultRatio = TEXTURE_RATIOS[formData.texture][0];
      if (!TEXTURE_RATIOS[formData.texture].includes(formData.feedingRatio)) {
        setFormData(prev => ({
          ...prev,
          feedingRatio: defaultRatio
        }));
      }
    }
  }, [formData.texture, isEditing]);

  // Check if starter was recently fed (less than 4 hours ago)
  useEffect(() => {
    if (!starter) return;
    
    const hoursSinceLastFeed = 
      (new Date().getTime() - new Date(starter.lastFed).getTime()) / (1000 * 60 * 60);
    setRecentlyFed(hoursSinceLastFeed < 4);
    
    // Set up timer to re-enable feed button after 4 hours
    if (hoursSinceLastFeed < 4) {
      const timeUntilFeedable = (4 - hoursSinceLastFeed) * 60 * 60 * 1000;
      const timer = setTimeout(() => {
        setRecentlyFed(false);
      }, timeUntilFeedable);
      
      return () => clearTimeout(timer);
    }
  }, [starter]);

  if (!starter) return null;

  const handleClose = () => {
    setIsEditing(false);
    setError(null);
    setLoading(false);
    setShowDeleteConfirm(false);
    onClose();
  };

  const handleSubmit = async () => {
    if (!starter?.id) return;
    setLoading(true);
    setError(null);

    try {
      const updatedData = {
        ...formData,
        storage: formData.managementStrategy === 'overboost' ? 'room' : formData.storage
      };

      await updateStarter(starter.id, updatedData);
      
      if (onUpdate) {
        onUpdate(starter.id, updatedData);
      }

      handleClose();
    } catch (err) {
      setError('Failed to update starter. Please try again.');
      console.error('Error updating starter:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async () => {
    if (!starter?.id) return;
    setLoading(true);
    setError(null);

    try {
      await deleteStarter(starter.id);
      handleClose();
    } catch (err) {
      setError('Failed to delete starter. Please try again.');
      console.error('Error deleting starter:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleFeed = async () => {
    if (recentlyFed || !starter?.id) return;

    try {
      const now = new Date();
      await updateStarter(starter.id, { lastFed: now });

      if (onUpdate) {
        onUpdate(starter.id, { lastFed: now });
      }

      setRecentlyFed(true);

      // Mark all unprocessed notifications for this starter as processed
      if (auth.currentUser) {
        const notificationsRef = collection(db, 'notifications');
        const q = query(
          notificationsRef,
          where('userId', '==', auth.currentUser.uid),
          where('type', '==', 'starter_feeding'),
          where('data.starterId', '==', starter.id),
          where('processed', '==', false)
        );

        const notificationDocs = await getDocs(q);
        const updatePromises = notificationDocs.docs.map(notificationDoc =>
          updateDoc(doc(db, 'notifications', notificationDoc.id), { processed: true })
        );

        await Promise.all(updatePromises);
        console.log(`Marked ${notificationDocs.size} notifications as processed for starter ${starter.id}`);
      }

      // Reset the recently fed status after 4 hours
      setTimeout(() => {
        setRecentlyFed(false);
      }, 4 * 60 * 60 * 1000);
    } catch (error) {
      console.error('Error feeding starter:', error);
      setError('Failed to feed starter. Please try again.');
    }
  };

  const formatDate = (date: Date) => {
    return new Date(date).toLocaleString('en-US', {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: 'numeric',
      minute: '2-digit'
    });
  };

  const strategy = MANAGEMENT_STRATEGIES[starter.managementStrategy || 'normal'];
  const feedingInterval = starter.storage === 'room'
    ? strategy.feedingInterval.room
    : strategy.feedingInterval.fridge;

  const hoursSinceLastFeed = 
    (new Date().getTime() - new Date(starter.lastFed).getTime()) / (1000 * 60 * 60);

  const getNextFeedingTime = () => {
    if (!starter.lastFed) return null;

    const lastFed = new Date(starter.lastFed);
    const nextFeeding = new Date(lastFed);
    nextFeeding.setHours(nextFeeding.getHours() + feedingInterval);

    const now = new Date();
    const diff = nextFeeding.getTime() - now.getTime();
    
    if (diff <= 0) return 'Overdue';
    
    const days = Math.floor(diff / (1000 * 60 * 60 * 24));
    const hours = Math.floor((diff % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
    
    if (days > 0) {
      return `${days}d ${hours}h`;
    }
    return `${hours}h`;
  };

  const health = checkStarterHealth(starter);
  const nextFeeding = getNextFeedingTime();
  const temperatureWarning = starter.roomTemperature ? getTemperatureWarning(starter.roomTemperature) : null;
  const StorageIcon = starter.storage === 'fridge' 
    ? ThermometerSnowflake 
    : starter.roomTemperature 
    ? GaugeCircle 
    : Thermometer;

  return (
    <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-[200] p-4">
      <div className="bg-gradient-to-br from-[#1C1C1C] to-[#888888] shadow-lg w-full max-w-lg max-h-[90vh] overflow-y-auto text-white">
        <div className="p-6 border-b border-white/10">
          <div className="flex justify-between items-center">
            <div className="flex-1">
              <h2 className="text-2xl text-white font-brand font-light italic">
                {isEditing ? 'Edit Starter' : starter.name}
              </h2>
            </div>
            <button 
              onClick={handleClose}
              className="text-white/70 hover:text-gray-600 transition-colors p-1 hover:bg-white/10 rounded-full"
            >
              <X className="w-6 h-6" />
            </button>
          </div>
        </div>

        <div className="p-6 space-y-6">
          {error && (
            <div className="p-4 bg-red-500/10 backdrop-blur-sm text-red-200 rounded-xl text-sm">
              {error}
            </div>
          )}

          {showDeleteConfirm ? (
            <div className="space-y-4">
              <div className="p-4 bg-red-500/40 backdrop-blur-sm rounded-xl border border-red-500/20">
                <h3 className="font-medium text-red-200">Delete Starter?</h3>
                <p className="mt-2 text-sm text-red-100/90">
                  This action cannot be undone. All associated baking sessions will also be deleted.
                </p>
              </div>

              <div className="flex justify-end gap-3">
                <button
                  onClick={() => setShowDeleteConfirm(false)}
                  className="px-4 py-2 border border-white/20 rounded-xl hover:bg-white/10"
                  disabled={loading}
                >
                  Cancel
                </button>
                <button
                  onClick={handleDelete}
                  disabled={loading}
                  className="px-4 py-2 bg-red-500/90 hover:bg-red-500 text-white rounded-xl flex items-center gap-2"
                >
                  {loading ? (
                    <>
                      <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                      Deleting...
                    </>
                  ) : (
                    <>
                      <Trash2 className="w-4 h-4" />
                      Delete Starter
                    </>
                  )}
                </button>
              </div>
            </div>
          ) : isEditing ? (
            <div className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <label className="block text-sm font-medium text-white/90 mb-1">
                  Name
                </label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  className="w-full px-4 py-3 bg-white/10 border border-white/20 rounded-xl text-white placeholder:text-white/50 focus:border-amber-300/50 focus:ring-2 focus:ring-amber-300/20"
                  placeholder="e.g., Whole Wheat Starter"
                  disabled={loading}
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-white/90 mb-2">
                  Management Strategy
                </label>
                <ManagementStrategySelector
                  value={formData.managementStrategy}
                  storage={formData.storage}
                  onChange={(strategy) => setFormData({ ...formData, managementStrategy: strategy })}
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-white/90 mb-2">
                  Texture
                </label>
                <TextureSelector
                  value={formData.texture}
                  onChange={(texture) => setFormData({ ...formData, texture })}
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-white/90 mb-2">
                  Feeding Ratio (Starter-Flour-Water)
                </label>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                  {TEXTURE_RATIOS[formData.texture].map((ratio) => (
                    <button
                      key={ratio}
                      onClick={() => setFormData({ ...formData, feedingRatio: ratio })}
                      className={`p-4 border rounded-xl flex items-center gap-3 transition-colors ${
                        formData.feedingRatio === ratio
                          ? 'border-amber-400/50 bg-gradient-to-b from-amber-400/20 to-amber-500/10'
                          : 'border-white/10 bg-white/5 hover:bg-white/10'
                      }`}
                    >
                      <span className="text-white">{ratio.replace(/-/g, ':')}</span>
                    </button>
                  ))}
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-white/90 mb-1">
                  Flour Type
                </label>
                <input
                  type="text"
                  value={formData.flourType}
                  onChange={(e) => setFormData({ ...formData, flourType: e.target.value })}
                  className="w-full px-4 py-3 bg-white/10 border border-white/20 rounded-xl text-white placeholder:text-white/50 focus:border-amber-300/50 focus:ring-2 focus:ring-amber-300/20"
                  placeholder="e.g., Whole Wheat, Rye, All-Purpose"
                  disabled={loading}
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-white/90 mb-2">
                  Storage Location
                </label>
                <StorageSelector
                  value={formData.storage}
                  onChange={(storage) => setFormData({ ...formData, storage })}
                  disableFridge={formData.managementStrategy === 'overboost'}
                  roomTemperature={formData.storage === 'room' ? formData.roomTemperature : undefined}
                  onRoomTemperatureChange={(temp) => setFormData({ ...formData, roomTemperature: temp })}
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-white/90 mb-1">
                  Last Fed
                </label>
                <input
                  type="datetime-local"
                  value={new Date(new Date(formData.lastFed).getTime() - new Date().getTimezoneOffset() * 60000).toISOString().slice(0, 16)}
                  onChange={(e) => setFormData({ ...formData, lastFed: new Date(e.target.value) })}
                  className="w-full px-4 py-3 bg-white/10 border border-white/20 rounded-xl text-white focus:border-amber-300/50 focus:ring-2 focus:ring-amber-300/20"
                  disabled={loading}
                />
              </div>
            </div>
          ) : (
            <div className="space-y-6">
              <div className="space-y-1">
                <div className="flex flex-col justify-between items-start">
                
                  {/* Right Section */}
                  <div className="w-full">
                    <StarterActivityGraph
                      hoursSinceLastFeed={hoursSinceLastFeed}
                      storage={starter.storage}
                      roomTemperature={starter.roomTemperature}
                      feedingRatio={starter.feedingRatio}
                      className="p-2 origin-right"
                    />
                  </div>

                  {/* Left Section */}
                  <div className="w-full">
                    {/* Feeding Info with Feed Button */}
                    <div className="flex flex-col">
                      <div className="flex justify-between items-center mt-4">
                          <div className="flex items-center gap-2">
                            <h3 className="text-sm font-medium text-white">Next feeding in</h3>
                            <p className="text-2xl text-amber-400 font-brand font-light italic">{nextFeeding}</p>
                           </div> 
                      </div>
                    </div>
                  </div>              
                </div>

                {health.status !== 'healthy' && (
                  <div className="flex items-start gap-2 p-2 bg-amber-500/10 backdrop-blur-sm rounded-lg border border-amber-500/20">
                    <AlertCircle className="w-4 h-4 flex-shrink-0 text-amber-300" />
                    <p className="text-sm text-amber-200">{health.message}</p>
                  </div>
                )}
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="p-4 bg-white/10 rounded-xl backdrop-blur-sm">
                  <p className="text-sm text-white/70">Texture</p>
                  <p className="font-medium mt-1 flex items-center gap-2">
                    {STARTER_TEXTURES.find(t => t.id === starter.texture)?.label}
                    <span className="text-amber-400">
                      ({starter.feedingRatio.replace(/-/g, ':')})
                    </span>
                  </p>
                </div>
                <div className="p-4 bg-white/10 rounded-xl backdrop-blur-sm">
                  <p className="text-sm text-white/70">Storage</p>
                  <div className="flex items-center gap-2 font-medium mt-1">
                    <StorageIcon className="w-4 h-4" />
                    <span>
                      {starter.storage === 'fridge' 
                        ? 'Refrigerated' 
                        : starter.roomTemperature 
                        ? `${starter.roomTemperature}°C`
                        : 'Room Temperature'}
                    </span>
                  </div>
                </div>
              </div>

              {temperatureWarning && (
                <div className="p-4 bg-amber-500/10 backdrop-blur-sm rounded-xl border border-amber-500/20">
                  <p className="text-sm text-amber-200">{temperatureWarning}</p>
                </div>
              )}

              <div>
                <h3 className="text-sm font-medium text-white/90 mb-2">Management Strategy</h3>
                <div className="p-4 bg-white/10 rounded-xl backdrop-blur-sm">
                  <p className="font-medium">{strategy.label}</p>
                  <p className="text-sm text-white/70 mt-1">{strategy.description}</p>
                  <div className="mt-2 text-sm text-white/80">
                    <p>Feed every {feedingInterval} hours</p>
                  </div>
                </div>
              </div>

              <div>
                <h3 className="text-sm font-medium text-white/90 mb-2">Flour Type</h3>
                <p className="p-4 bg-white/10 rounded-xl backdrop-blur-sm">
                  {starter.flourType}
                </p>
              </div>

              <div>
                <h3 className="text-sm font-medium text-white/90 mb-2">Last Fed</h3>
                <p className="p-4 bg-white/10 rounded-xl backdrop-blur-sm">
                  {formatDate(starter.lastFed)}
                </p>
              </div>

              <div>
                <h3 className="text-sm font-medium text-white/90 mb-2">Created On</h3>
                <p className="p-4 bg-white/10 rounded-xl backdrop-blur-sm">
                  {formatDate(starter.createdAt)}
                </p>
              </div>
            </div>
          )}
        </div>

        <div className="p-6 border-t border-white/10 bg-white/5">
          {!showDeleteConfirm && (
            <div className="flex justify-between items-center">
              {isEditing ? (
                <button
                  onClick={() => setShowDeleteConfirm(true)}
                  className="flex items-center gap-2 px-4 py-2 text-red-300 hover:text-red-200 hover:bg-red-500/10 rounded-xl transition-colors"
                >
                  <Trash2 className="w-4 h-4" />
                  Delete Starter
                </button>
              ) : (
                <div></div>
              )}
              <div className="flex gap-3">
                {isEditing ? (
                  <>
                    <button
                      onClick={() => setIsEditing(false)}
                      disabled={loading}
                      className="px-4 py-2 text-white/70 hover:text-white border border-white/20 rounded-xl hover:bg-white/10 disabled:opacity-50 transition-colors"
                    >
                      Cancel
                    </button>
                    <button
                      onClick={handleSubmit}
                      disabled={loading || !formData.name || !formData.flourType}
                      className={`px-4 py-2 rounded-xl flex items-center gap-2 transition-all duration-300 ${
                        loading
                          ? 'bg-amber-400/50 cursor-not-allowed'
                          : formData.name && formData.flourType
                          ? 'bg-gradient-to-r from-amber-500/90 to-amber-600/90 hover:from-amber-500 hover:to-amber-600'
                          : 'bg-white/10 text-white/50 cursor-not-allowed'
                      }`}
                    >
                      {loading && (
                        <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                      )}
                      {loading ? 'Saving...' : 'Save Changes'}
                    </button>
                  </>
                ) : (
                  <button
                    onClick={() => setIsEditing(true)}
                    disabled={loading}
                    className="px-6 py-2 bg-gradient-to-r from-amber-500/90 to-amber-600/90 hover:from-amber-500 hover:to-amber-600 text-white rounded-xl disabled:opacity-50 transition-all duration-300"
                  >
                    Edit Starter
                  </button>
                )}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}